import { DateTime } from "luxon";
import { ICompetition } from "../competition/Competition";
import { IGame, IOfficials } from "../game/Game";
import { IGameConfiguration } from "../game/GameConfiguration";
import { IPerson } from "../persons/person";
import { ITeam } from "../team/Team";
import { IVenue } from "../venue/Venue";
import { IShiftEnrolment, IShiftSessionEnrolment } from "./ShiftEnrolment";
import { ShiftSessionStatus } from "./enums/ShiftSessionStatus";

export enum ShiftDaysOfWeek {
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6,
  Sunday = 7,
}
export namespace ShiftDaysOfWeek {
  export function all() {
    return [
      ShiftDaysOfWeek.Monday,
      ShiftDaysOfWeek.Tuesday,
      ShiftDaysOfWeek.Wednesday,
      ShiftDaysOfWeek.Thursday,
      ShiftDaysOfWeek.Friday,
      ShiftDaysOfWeek.Saturday,
      ShiftDaysOfWeek.Sunday,
    ];
  }
  export function toString(dayOfWeek: ShiftDaysOfWeek) {
    switch (dayOfWeek) {
      case ShiftDaysOfWeek.Monday:
        return "Monday";
      case ShiftDaysOfWeek.Tuesday:
        return "Tuesday";
      case ShiftDaysOfWeek.Wednesday:
        return "Wednesday";
      case ShiftDaysOfWeek.Thursday:
        return "Thursday";
      case ShiftDaysOfWeek.Friday:
        return "Friday";
      case ShiftDaysOfWeek.Saturday:
        return "Saturday";
      case ShiftDaysOfWeek.Sunday:
        return "Sunday";
    }
    return "Unknown";
  }

  export function getShiftPriorityFromDayOfWeek(dayOfWeek: ShiftDaysOfWeek) {
    return dayOfWeek * 10;
  }

  export function guessDayOfWeekInString(
    value: string,
  ): ShiftDaysOfWeek | undefined {
    const lvalue = value.toLowerCase();
    const dayOfWeek = ShiftDaysOfWeek.all().filter((val) =>
      lvalue.includes(ShiftDaysOfWeek.toString(val).toLowerCase()),
    )[0];
    return dayOfWeek;
  }
}

export enum OrderingType {
  dayOfWeek = "dayOfWeek",
  custom = "custom",
}

export namespace OrderingType {
  export function all(): OrderingType[] {
    return [OrderingType.dayOfWeek, OrderingType.custom];
  }

  export function toDisplayString(orderingType: OrderingType) {
    switch (orderingType) {
      case OrderingType.dayOfWeek:
        return "Day of Week";
      case OrderingType.custom:
        return "Custom";
    }
    return "Unknown";
  }
}

export class IDateRange {
  start: Date;

  finish: Date;

  comment?: string;
}

export class IShiftSession extends IDateRange {
  _id: string;

  shift: string;

  supervisors?: { [venueId: string]: IShiftSupervisor[] };
  administrators?: { [venueId: string]: IShiftAdmin[] }

  confirmation?: {
    token: string;
    date: Date;
    person: string;
  };

  paymentStatus?: 'unpaid' | 'partial' | 'paid';
}

export interface IShiftSupervisor {
  person: string | null;
  appointment: string;
}


export interface IShiftAdmin extends IShiftSupervisor { }


export interface IDocumentPaysheetRange extends IDateRange {
  name: string;
}

export interface INamedRange extends IDateRange {
  name: string;
}

export interface IShift {
  _id: string | any;
  key?: string | null; // used internally
  name: string;
  associationId: string;
  seasonId: string | null;
  sessions: IShiftSession[];
  person: string;
  enrolments: IShiftEnrolment[];
  timestamp: Date;
  venues: string[];
  warnings?: string[];
  start: Date;
  finish: Date;
  gridView?: "venue" | "competition";
  ordering?: {
    type: OrderingType;
    value: number;
  };
  supervisorDefaultTimes?: { start: string; finish: string }[];
  includeAssociationPermanentReferees?: boolean;
}

export interface IFilledShiftContainer {
  key: string;
  persons: IPerson[];
  games: IGame[];
  teams: ITeam[];
  venues: IVenue[];
  competitions: ICompetition[];
  warnings: string[];
  gameConfigs: IGameConfiguration[];
  range: IDateRange;
  availabilities?: { [person: string]: IDateRange[] };
}

export interface IFilledSessionShiftContainer extends IFilledShiftContainer {
  session: IShiftSession;
}

export interface IProcessedGame {
  _id: any;

  dateStr: string;
  date: DateTime;
  slot: DateTime;
  duration: number;
  team1: string;
  team2: string;
  competition: string;
  venue: string;
  court: string;

  // persons: string[] // for keys
  referees: IOfficials;
}

export interface IGamePlaceholder {
  id: string;
  length: number;
}

export interface IProcessedSessionContainer {
  key: string;
  session: IShiftSession;

  enrolments: IShiftSessionEnrolment[];
  venueIds: string[];
  gameIds: string[]; // as a convenience

  gameMapByVenue?: {
    [venueId: string]: [
      DateTime | null, // slot
      { [court: string]: IGamePlaceholder | null },
    ][];
  };
  gameMap?: [
    DateTime | null,
    { [venueCourtId: string]: IGamePlaceholder | null },
  ][];

  skippedGames: IProcessedGame[];
}

export interface IUpcomingSessionTransport {
  shiftId: string;
  sessionId: string;
  rosterToken?: string;
  date: string;
  name?: string;
  status: ShiftSessionStatus;
  start?: string;
  finish?: string;
}
//   return <IFilledShiftContainer>{ shift, games, shiftRange: range, persons, venues, competitions, teams, warnings: shift.warnings }
